// Regla de margen de ganancia del súper (retail): tipos, validación y fórmula costo -> precio CASH.
// Módulo PURO: sin "use server", sin imports de servidor, importable desde el cliente.
// Spec: docs/superpowers/specs/2026-10-27-retail-margen-de-ganancia-design.md (§3.2, §3.4, §4).
//
// Norma de dinero: lo que se calcula aquí es el precio CASH. Quien lo guarde como precio de
// tarjeta debe grossear UNA vez con `netToCard` (lib/utils/pricing.ts). Este módulo no conoce
// ninguna tasa de descuento en efectivo.

export type MarginBasis = "price" | "cost"
export type MarginTierId = 1 | 2 | 3
export type MarginTier = { id: MarginTierId; name: string; pct: number }
export type RetailMargin = { basis: MarginBasis; tiers: MarginTier[] }
export type MarginRule = { pct: number; basis: MarginBasis }

/** Máximo de niveles de margen con nombre por local (spec §3.2). */
export const MARGIN_TIER_MAX = 3
/** Costo máximo aceptado (numeric(10,4) en la base). Ver `costOutOfRange`. */
export const MAX_COST = 999999.9999

export type RetailMarginError =
  | "invalid_basis"
  | "invalid_tiers"
  | "duplicate_tier"
  | "invalid_name"
  | "invalid_pct"

/** Tope del porcentaje: sobre precio no puede llegar a 100 %; sobre costo, 1000 %. */
export function pctLimit(basis: MarginBasis): number {
  return basis === "price" ? 90 : 1000
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v)
}

/**
 * Valida la regla cruda (jsonb de `location.features.retail_margin`).
 * Orden: objeto, basis, tiers (1..3, ids 1|2|3, con el 1), ids distintos, nombres, pct.
 * Devuelve el primer error o null si es válida (spec §3.2, §3.4).
 */
export function retailMarginError(raw: unknown): RetailMarginError | null {
  if (!isRecord(raw)) return "invalid_basis"
  const basis = raw.basis
  if (basis !== "price" && basis !== "cost") return "invalid_basis"

  const tiers = raw.tiers
  if (!Array.isArray(tiers) || tiers.length < 1 || tiers.length > MARGIN_TIER_MAX) return "invalid_tiers"
  if (!tiers.every((t) => isRecord(t) && (t.id === 1 || t.id === 2 || t.id === 3))) return "invalid_tiers"
  if (!tiers.some((t) => (t as Record<string, unknown>).id === 1)) return "invalid_tiers"

  const ids = tiers.map((t) => (t as Record<string, unknown>).id)
  if (new Set(ids).size !== ids.length) return "duplicate_tier"

  const names = tiers.map((t) => (t as Record<string, unknown>).name)
  if (!names.every((n) => typeof n === "string" && n.trim() !== "")) return "invalid_name"
  const keys = (names as string[]).map((n) => n.trim().toLowerCase())
  if (new Set(keys).size !== keys.length) return "invalid_name"

  const limit = pctLimit(basis)
  for (const t of tiers as Record<string, unknown>[]) {
    const pct = t.pct
    if (typeof pct !== "number" || !Number.isFinite(pct)) return "invalid_pct"
    if (pct < 0 || pct > limit) return "invalid_pct"
    // Como mucho dos decimales. Con tolerancia: 0.07 * 100 = 7.000000000000001 en coma flotante.
    if (Math.abs(pct * 100 - Math.round(pct * 100)) > 1e-6) return "invalid_pct"
  }
  return null
}

/** Regla normalizada, o null si `raw` es null/undefined o no es válida. */
export function parseRetailMargin(raw: unknown): RetailMargin | null {
  if (raw === null || raw === undefined) return null
  if (retailMarginError(raw) !== null) return null
  const r = raw as RetailMargin
  return {
    basis: r.basis,
    // Canónica: la base exige `pct = round(pct, 2)` en numeric y no tiene la tolerancia de
    // arriba (35.000000001 pasa aquí y da invalid_rule allá), así que se manda redondeado.
    // El nombre va sin espacios de los bordes (la unicidad ya se comparó con trim).
    tiers: r.tiers.map((t) => ({ id: t.id, name: t.name.trim(), pct: Math.round(t.pct * 100) / 100 })),
  }
}

/** Regla (pct + base) del nivel `tier`, o null si ese nivel no existe. */
export function ruleForTier(margin: RetailMargin, tier: number): MarginRule | null {
  const t = margin.tiers.find((x) => x.id === tier)
  return t ? { pct: t.pct, basis: margin.basis } : null
}

// ---------------------------------------------------------------------------
// Costo -> precio CASH (spec §4). Todo en enteros: nada de coma flotante en el redondeo.
// ---------------------------------------------------------------------------

/**
 * Costo normalizado a 4 decimales (la precisión de `numeric(10,4)`).
 * Acepta número o texto numérico ("1.0026", como llega un numeric de Postgres).
 * null si falta, no es un número finito o es <= 0. No aplica el tope: eso es `costOutOfRange`.
 */
export function normalizeCost(cost: unknown): number | null {
  let n: number
  if (typeof cost === "number") n = cost
  else if (typeof cost === "string" && cost.trim() !== "") n = Number(cost)
  else return null
  if (!Number.isFinite(n)) return null
  // Redondear ANTES de rechazar: un costo positivo que redondea a 0 (p. ej. 0.00004) no es un costo.
  const rounded = roundCost4(n)
  return rounded > 0 ? rounded : null
}

/**
 * Redondeo a 4 decimales que da lo MISMO que `round(x::numeric, 4)` de Postgres: mitad hacia
 * arriba sobre la representación DECIMAL del número (spec §3.5: la guarda de la RPC compara
 * `round(items.cost, 4) = r.cost`; si aquí sale otro valor, esa fila se salta para siempre).
 *
 * `Math.round(n * 10000)` NO sirve: el producto se hace en binario y una mitad decimal casi
 * nunca es exacta. 1.01195 * 10000 = 10119.499999999998 → 1.0119, pero Postgres da 1.0120;
 * 0.00015 → 0.0001 contra 0.0002. Pasaba en ~6,9 % de los costos con 5 decimales «.…5».
 * `toPrecision(15)` corta el ruido binario (un double tiene 15–17 cifras significativas
 * fiables) y deja el valor decimal que se quiso escribir antes de redondear.
 * Los negativos se rechazan después (`> 0`), así que el sentido del redondeo de la mitad
 * negativa no importa. Límite: un costo con más de 15 cifras significativas ya no cabe en un
 * double (ni llega entero por JSON); `item_variants.cost` es numeric(10,4) y `items.cost`,
 * aunque es numeric sin escala, en la práctica trae 4 decimales o menos.
 */
function roundCost4(n: number): number {
  return Math.round(Number((n * 10000).toPrecision(15))) / 10000
}

/** true si el costo (ya normalizado) excede el máximo que cabe en la base. */
export function costOutOfRange(cost: number): boolean {
  return cost > MAX_COST
}

/**
 * Precio CASH que resulta de aplicar la regla al costo, con techo al centavo:
 * el precio nunca queda por debajo del margen pedido (spec §4.2).
 *  - base "cost":  costo x (1 + pct/100)
 *  - base "price": costo / (1 - pct/100)
 * null si el costo es inválido o está fuera de rango, o si el pct no es finito, es negativo o
 * supera `pctLimit(basis)` (la base "price" nunca llega a pct >= 100).
 * El llamador que guarde precio de tarjeta debe grossear con `netToCard`.
 */
export function cashPriceFromCost(cost: unknown, rule: MarginRule): number | null {
  const c = normalizeCost(cost)
  if (c === null || costOutOfRange(c)) return null
  // Segunda red (spec §4.1): una regla fuera de rango o no finita no produce precio.
  if (!Number.isFinite(rule.pct) || rule.pct < 0 || rule.pct > pctLimit(rule.basis)) return null

  const cost10k = Math.round(c * 10000)
  const pct100 = Math.round(rule.pct * 100)
  let cents: number
  if (rule.basis === "cost") {
    const n = cost10k * (10000 + pct100)
    cents = Math.floor(n / 1e6) + (n % 1e6 > 0 ? 1 : 0)
  } else {
    const d = 10000 - pct100
    if (d <= 0) return null
    const n = cost10k * 100
    cents = Math.floor(n / d) + (n % d > 0 ? 1 : 0)
  }
  return cents / 100
}

/** true si el precio cash tecleado coincide, al centavo, con el que da la regla para ese costo. */
export function followsMargin(cashTyped: number, cost: unknown, rule: MarginRule): boolean {
  const price = cashPriceFromCost(cost, rule)
  if (price === null) return false
  return Math.round(cashTyped * 100) === Math.round(price * 100)
}
